import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { PhotoEditor } from './PhotoEditor';

const API = window.__SG_API_BASE__;

const blobToFile = (blob, name = 'photo.jpg') =>
  new File([blob], name, { type: blob.type || 'image/jpeg' });

const uploadClosetItem = async (file) => {
  const fd = new FormData();
  fd.append('images', file);
  return axios.post(`${API}/api/closet/items/batch`, fd, { withCredentials: true });
};

const ActionButton = ({ icon, label, sub, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="w-full flex items-center gap-4 px-5 py-4 bg-white border border-[#E5E4E2] rounded-2xl hover:border-[#0D0D0D] transition-colors text-left"
  >
    <span className="w-10 h-10 rounded-full flex items-center justify-center text-lg bg-[#F7F5F1] text-[#0D0D0D]">
      {icon}
    </span>
    <span className="flex-1">
      <span className="block font-sans text-sm text-[#0D0D0D]">{label}</span>
      {sub && <span className="block font-sans text-[10px] uppercase tracking-[0.18em] text-[#8E8A84] mt-0.5">{sub}</span>}
    </span>
  </button>
);

/**
 * Mobile-first action sheet triggered by the floating camera FAB.
 * Supports five modes: take photo, upload, add closet item, fit check, edit
 * existing photo. All capture paths use native HTML5 file inputs; the
 * resulting image is routed through PhotoEditor (which strips EXIF via
 * canvas redraw) before reaching the server.
 */
export const CameraActionSheet = ({ open, onClose }) => {
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const editExistingRef = useRef(null);
  const [editingSrc, setEditingSrc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset transient state every time the sheet opens.
  useEffect(() => {
    if (open) {
      setPermissionDenied(false);
    }
  }, [open]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onerror = () => {
      setPermissionDenied(true);
      toast.error('Could not read the file. Please try again.');
    };
    reader.onload = (e) => setEditingSrc(e.target?.result || null);
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) {
      // User cancelled the native picker — no-op.
      return;
    }
    handleFile(file);
  };

  const handleSaveEdited = async (blob) => {
    setSaving(true);
    try {
      // RC1.2.0 — Sprint A correction: Fit Check entry no longer routes through
      // this action sheet's camera. The 'Fit Check' tile navigates directly to
      // /fit-check, where the user picks Take/Upload from the Fit Check page
      // itself. Closet capture remains the only destination handled here.
      const file = blobToFile(blob, `closet-${Date.now()}.jpg`);
      const resp = await uploadClosetItem(file);
      toast.success('Saved to your closet.');
      setEditingSrc(null);
      onClose();
      // RC1.1.4 reality-fix — pass the new item id so /closet can fire the
      // post-save action sheet (View / Edit / Post to Feed / Share / Ask
      // Stylist / Find Similar) immediately after a single capture, not
      // only after a batch upload.
      const newId = resp?.data?.id || resp?.data?.item_id
        || (resp?.data?.items && resp.data.items[0]?.id);
      navigate('/closet', { state: { showPostSave: true, newItemId: newId } });
    } catch (err) {
      console.error(err);
      toast.error('Could not save your photo. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open && !editingSrc) return null;

  return (
    <>
      {/* Hidden file inputs — always rendered so refs are valid. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleInputChange}
        className="hidden"
        data-testid="camera-action-sheet-camera-input"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        data-testid="camera-action-sheet-gallery-input"
      />
      <input
        ref={editExistingRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
        data-testid="camera-action-sheet-edit-existing-input"
      />

      {open && !editingSrc && (
        <div
          className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center"
          role="presentation"
          onClick={onClose}
          data-testid="camera-action-sheet"
        >
          <div
            className="bg-[#F7F5F1] w-full md:max-w-md md:rounded-3xl rounded-t-3xl p-5 md:p-7 pb-8 md:pb-7 space-y-3 shadow-2xl"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#C7A96B]">Style Camera</p>
                <h3 className="font-serif text-2xl text-[#0D0D0D]">Capture & save</h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-2xl text-[#8E8A84] hover:text-[#0D0D0D] px-2"
                data-testid="camera-action-sheet-close"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <ActionButton
              icon="📷"
              label="Take a photo"
              sub="Use your phone camera"
              onClick={() => cameraRef.current?.click()}
              testId="camera-action-sheet-take-photo"
            />
            <ActionButton
              icon="🖼"
              label="Upload image"
              sub="Pick from your gallery"
              onClick={() => galleryRef.current?.click()}
              testId="camera-action-sheet-upload"
            />
            <ActionButton
              icon="＋"
              label="Add closet item manually"
              sub="Enter details, skip the camera"
              onClick={() => { onClose(); navigate('/closet'); }}
              testId="camera-action-sheet-manual-add"
            />
            <ActionButton
              icon="✦"
              label="Fit Check"
              sub="Get feedback on a full outfit"
              onClick={() => { onClose(); navigate('/fit-check?mode=new'); }}
              testId="camera-action-sheet-fit-check"
            />
            <ActionButton
              icon="✎"
              label="Edit existing photo"
              sub="Crop or rotate an image"
              onClick={() => editExistingRef.current?.click()}
              testId="camera-action-sheet-edit-existing"
            />

            {permissionDenied && (
              <div className="mt-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-2xl" data-testid="camera-action-sheet-permission-denied">
                <p className="font-sans text-xs text-amber-800">
                  Camera permission denied. Tap <strong>Upload image</strong> to pick a photo from your gallery instead.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              className="w-full mt-2 py-3 font-sans text-[11px] uppercase tracking-[0.2em] text-[#8E8A84] hover:text-[#0D0D0D]"
              data-testid="camera-action-sheet-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {editingSrc && (
        <PhotoEditor
          src={editingSrc}
          aspect={4 / 5}
          onCancel={() => setEditingSrc(null)}
          onSave={handleSaveEdited}
        />
      )}

      {saving && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center" data-testid="camera-action-sheet-saving">
          <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </>
  );
};

export default CameraActionSheet;
